import {
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  Add,
  Dashboard,
  DeleteOutline,
  Edit,
  Group,
  MoreVert,
} from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { taskRequest } from "./api";
import BoardMembersDialog from "./BoardMembersDialog";

export default function BoardPicker({ onSelect, initialBoardId }) {
  const { t } = useTranslation();
  const [boards, setBoards] = useState([]);
  useEffect(() => {
    const board = boards.find((item) => item.id === initialBoardId);
    if (board) onSelect(board);
  }, [boards, initialBoardId, onSelect]);
  const [boardMenu, setBoardMenu] = useState(null);
  const [membersBoard, setMembersBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [deletingBoard, setDeletingBoard] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const requestId = useRef(0);
  const mutation = useRef(false);

  const load = useCallback(async () => {
    const current = ++requestId.current;
    setLoading(true);
    setError("");
    try {
      const result = await taskRequest("get_task_boards");
      if (current === requestId.current) setBoards(result.boards);
    } catch (err) {
      if (current === requestId.current) setError(err.message);
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      requestId.current += 1;
    };
  }, [load]);

  const saveBoard = async (event) => {
    event.preventDefault();
    if (mutation.current || !name.trim()) return;
    mutation.current = true;
    setSaving(true);
    setSaveError("");
    let board;
    try {
      const result = await taskRequest("save_task_board", {
        name: name.trim(),
        ...(editingBoard ? { boardId: editingBoard.id } : {}),
      });
      board = result.board;
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
      mutation.current = false;
    }
    if (board && editingBoard) {
      setBoards((previous) =>
        previous.map((item) => (item.id === board.id ? board : item)),
      );
      setEditingBoard(null);
      setCreating(false);
    } else if (board) onSelect(board);
  };

  const deleteBoard = async () => {
    if (mutation.current || !deletingBoard) return;
    mutation.current = true;
    setSaving(true);
    setDeleteError("");
    try {
      await taskRequest("delete_task_board", { boardId: deletingBoard.id });
      setBoards((previous) =>
        previous.filter((board) => board.id !== deletingBoard.id),
      );
      setDeletingBoard(null);
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setSaving(false);
      mutation.current = false;
    }
  };

  return (
    <div style={{ width: "100%", minWidth: 0 }}>
      <Breadcrumbs>
        <Typography>{t("tasks.header")}</Typography>
      </Breadcrumbs>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          margin: "16px 0",
        }}
      >
        <Typography component="h1" variant="h5" style={{ flexGrow: 1 }}>
          {t("tasks.boards")}
        </Typography>
      </div>
      <Button
        style={{ marginBottom: 10 }}
        color="primary"
        variant="contained"
        startIcon={<Add />}
        disabled={loading || saving}
        onClick={() => {
          setName("");
          setSaveError("");
          setEditingBoard(null);
          setCreating(true);
        }}
      >
        {t("tasks.createBoard")}
      </Button>
      {error && (
        <Alert severity="error" role="alert" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <CircularProgress aria-label={t("tasks.loadingBoards")} />
      ) : (
        <>
          {!boards.length && !error && (
            <Paper style={{ padding: 32, textAlign: "center" }}>
              <Typography color="textSecondary">
                {t("tasks.emptyBoards")}
              </Typography>
            </Paper>
          )}
          {!!boards.length && (
            <>
              <Typography color="textSecondary" paragraph>
                {t("tasks.selectBoard")}
              </Typography>
              <Grid container spacing={2}>
                {boards.map((board) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={board.id}>
                    <Card style={{ position: "relative" }}>
                      <CardActionArea
                        disabled={saving}
                        onClick={() => onSelect(board)}
                      >
                        <CardContent
                          style={{ minHeight: 100, paddingRight: 56 }}
                        >
                          <Dashboard color="primary" />
                          <Typography
                            component="h2"
                            variant="h6"
                            style={{ overflowWrap: "anywhere" }}
                          >
                            {board.name}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                      <IconButton
                        id={`board-menu-button-${board.id}`}
                        disabled={saving}
                        style={{ position: "absolute", top: 4, right: 4 }}
                        aria-label={`${t("tasks.boardActions")}: ${board.name}`}
                        aria-haspopup="menu"
                        aria-controls={
                          boardMenu && boardMenu.board.id === board.id
                            ? "board-actions-menu"
                            : undefined
                        }
                        aria-expanded={
                          !!boardMenu && boardMenu.board.id === board.id
                        }
                        onClick={(event) =>
                          setBoardMenu({ anchorEl: event.currentTarget, board })
                        }
                      >
                        <MoreVert />
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      )}
      <Menu
        id="board-actions-menu"
        anchorEl={boardMenu ? boardMenu.anchorEl : null}
        open={!!boardMenu}
        onClose={() => setBoardMenu(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{
          "aria-labelledby": boardMenu
            ? `board-menu-button-${boardMenu.board.id}`
            : undefined,
        }}
      >
        {boardMenu && [
          <MenuItem
            key="members"
            disabled={saving}
            aria-label={`${t("tasks.members")}: ${boardMenu.board.name}`}
            onClick={() => {
              setMembersBoard(boardMenu.board);
              setBoardMenu(null);
            }}
          >
            <ListItemIcon>
              <Group fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("tasks.members")} />
          </MenuItem>,
          <MenuItem
            key="rename"
            disabled={saving}
            aria-label={`${t("tasks.renameBoard")}: ${boardMenu.board.name}`}
            onClick={() => {
              setName(boardMenu.board.name);
              setSaveError("");
              setEditingBoard(boardMenu.board);
              setCreating(true);
              setBoardMenu(null);
            }}
          >
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("tasks.renameBoard")} />
          </MenuItem>,
          <MenuItem
            key="delete"
            disabled={saving}
            aria-label={`${t("tasks.deleteBoard")}: ${boardMenu.board.name}`}
            onClick={() => {
              setDeleteError("");
              setDeletingBoard(boardMenu.board);
              setBoardMenu(null);
            }}
          >
            <ListItemIcon>
              <DeleteOutline fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("tasks.deleteBoard")} />
          </MenuItem>,
        ]}
      </Menu>
      {membersBoard && (
        <BoardMembersDialog
          board={membersBoard}
          onClose={() => setMembersBoard(null)}
        />
      )}
      <Dialog
        open={creating}
        onClose={() => {
          if (!saving) setCreating(false);
        }}
        fullWidth
        maxWidth="xs"
        aria-labelledby="create-board-title"
      >
        <form onSubmit={saveBoard}>
          <DialogTitle id="create-board-title">
            {t(editingBoard ? "tasks.renameBoard" : "tasks.createBoard")}
          </DialogTitle>
          <DialogContent>
            {saveError && (
              <Alert severity="error" role="alert">
                {saveError}
              </Alert>
            )}
            {!editingBoard && (
              <Typography paragraph>{t("tasks.creatorMember")}</Typography>
            )}
            <TextField
              id="task-board-name"
              autoFocus
              required
              fullWidth
              label={t("tasks.boardName")}
              value={name}
              disabled={saving}
              inputProps={{ maxLength: 255 }}
              onChange={(event) => setName(event.target.value)}
            />
          </DialogContent>
          <DialogActions>
            {saving && <CircularProgress size={20} />}
            <Button disabled={saving} onClick={() => setCreating(false)}>
              {t("tasks.close")}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={saving || !name.trim()}
            >
              {t(editingBoard ? "tasks.save" : "tasks.createBoard")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog
        open={!!deletingBoard}
        onClose={() => {
          if (!saving) setDeletingBoard(null);
        }}
        fullWidth
        maxWidth="sm"
        aria-labelledby="delete-board-title"
        aria-describedby="delete-board-description"
      >
        <DialogTitle id="delete-board-title">
          {t("tasks.deleteBoard")}
        </DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" role="alert">
              {deleteError}
            </Alert>
          )}
          <Typography id="delete-board-description">
            {t("tasks.deleteBoardConfirmation", {
              name: deletingBoard ? deletingBoard.name : "",
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          {saving && <CircularProgress size={20} />}
          <Button disabled={saving} onClick={() => setDeletingBoard(null)}>
            {t("tasks.cancel")}
          </Button>
          <Button
            color="secondary"
            variant="contained"
            disabled={saving}
            onClick={deleteBoard}
          >
            {t("tasks.confirmDeleteBoard")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
